import React, { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";

// UK date field: shows/accepts DD/MM/YYYY, emits ISO YYYY-MM-DD via onChange({ target: { value } }).
const toUK = (iso) => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");
const toISO = (uk) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(uk);
  if (!m) return null;
  const d = new Date(Date.UTC(+m[3], +m[2] - 1, +m[1]));
  return d.getUTCDate() === +m[1] && d.getUTCMonth() === +m[2] - 1 ? `${m[3]}-${m[2]}-${m[1]}` : null;
};
const mask = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join("/");
};
const isoOf = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export default function DateInput({ value, onChange, name, min, max, disabled, required, className = "", style, placeholder = "DD/MM/YYYY", ...rest }) {
  const [text, setText] = useState(toUK(value));
  const [open, setOpen] = useState(false);
  useEffect(() => { setText(toUK(value)); }, [value]);
  const emit = (iso) => onChange && onChange({ target: { value: iso, name } });
  const inRange = (iso) => (!min || iso >= min) && (!max || iso <= max);

  const onType = (e) => {
    const t = mask(e.target.value);
    setText(t);
    if (!t) return emit("");
    const iso = toISO(t);
    if (iso && inRange(iso)) emit(iso);
  };
  const onBlur = () => {
    const iso = toISO(text);
    if (text && (!iso || !inRange(iso))) setText(toUK(value));
  };

  return (
    <span className={`relative inline-flex items-center ${/\bw-auto|\bw-\[|\bw-\d|!w-/.test(className) ? "" : "w-full"}`}>
      <input
        {...rest}
        type="text" inputMode="numeric" autoComplete="off" name={name}
        value={text} onChange={onType} onBlur={onBlur}
        placeholder={placeholder} disabled={disabled} required={required}
        pattern="\d{2}/\d{2}/\d{4}" title="Date as DD/MM/YYYY"
        className={`${className} !pr-8`} style={style}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} aria-label="Open calendar"
            className="absolute right-1.5 p-1 rounded muted hover:text-[var(--ink)] disabled:opacity-40"
            data-testid={rest["data-testid"] ? `${rest["data-testid"]}-calendar` : undefined}>
            <CalendarDays size={14} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single" weekStartsOn={1}
            selected={value ? new Date(`${value.slice(0, 10)}T12:00:00`) : undefined}
            defaultMonth={value ? new Date(`${value.slice(0, 10)}T12:00:00`) : undefined}
            disabled={(d) => !inRange(isoOf(d))}
            onSelect={(d) => { if (d) { emit(isoOf(d)); setOpen(false); } }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </span>
  );
}
